import { useState, useEffect } from "react";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import moment from "moment";

import MDBox from "../../../components/MDBox";
import MDModal from "../../../components/MDModal";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";

import { getDevice, putDevice } from "../../../api/devices";

import { getOrder } from "../../../api/orders";

import PropTypes from "prop-types";

import { useDispatch, useSelector } from "react-redux";

import PreparationContractual from "./PreparationContractual";
import PreparationProvisional from "./PreparationProvisional";
import PreparationReal from "./PreparationReal";
import PreparationCommentary from "./PreparationCommentary";

import { useAuth } from "context/AuthContext";
import useAuthCheck from "config/useAuthCheck";

import { setCurrentDevices } from "../../../redux/reducers/devicesReducer";

import { setCurrentOrder } from "../../../redux/reducers/ordersReducer";

export default function EditPreparation({ device = null, edit = false, renderStep = null }) {
  useAuthCheck();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);

  const [status, setStatus] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [assemblyStartContractualDate, setAssemblyStartContractualDate] = useState(null);
  const [madContractualDate, setMadContractualDate] = useState(null);
  const [assemblyStartProvisionalDate, setAssemblyStartProvisionalDate] = useState(null);
  const [madProvisionalDate, setMadProvisionalDate] = useState(null);
  const [assemblyStartRealDate, setAssemblyStartRealDate] = useState(null);
  const [madRealDate, setMadRealDate] = useState(null);
  const [exFactoryRealDate, setExFactoryRealDate] = useState(null);
  const [siteDeliveryRealDate, setSiteDeliveryRealDate] = useState(null);
  const [prepaCommentary, setPrepaCommentary] = useState("");

  const currentOrder = useSelector((state) => state.orders.currentOrder);

  const dispatch = useDispatch();

  useEffect(() => {
    if (edit && device) {
      setDeviceId(device.device_id);
      setStatus(device.status);
      setAssemblyStartContractualDate(device.assembly_start_contractual_date);
      setMadContractualDate(device.mad_contractual_date);
      setAssemblyStartProvisionalDate(device.assembly_start_provisional_date);
      setMadProvisionalDate(device.mad_provisional_date);
      setAssemblyStartRealDate(device.assembly_start_real_date);
      setMadRealDate(device.mad_real_date);
      setExFactoryRealDate(device.ex_factory_real_date);
      setSiteDeliveryRealDate(device.site_delivery_real_date);
      setPrepaCommentary(device.prepa_commentary);
    }
  }, [edit, device]);

  async function updateDeviceAPI() {
    const device = {
      status: status,
      mad_contractual_date: madContractualDate
        ? moment(madContractualDate).format("YYYY-MM-DD")
        : null,
      assembly_start_contractual_date: assemblyStartContractualDate
        ? moment(assemblyStartContractualDate).format("YYYY-MM-DD")
        : null,
      mad_provisional_date: madProvisionalDate
        ? moment(madProvisionalDate).format("YYYY-MM-DD")
        : null,
      assembly_start_provisional_date: assemblyStartProvisionalDate
        ? moment(assemblyStartProvisionalDate).format("YYYY-MM-DD")
        : null,
      mad_real_date: madRealDate ? moment(madRealDate).format("YYYY-MM-DD") : null,
      assembly_start_real_date: assemblyStartRealDate
        ? moment(assemblyStartRealDate).format("YYYY-MM-DD")
        : null,
      ex_factory_real_date: exFactoryRealDate
        ? moment(exFactoryRealDate).format("YYYY-MM-DD")
        : null,
      site_delivery_real_date: siteDeliveryRealDate
        ? moment(siteDeliveryRealDate).format("YYYY-MM-DD")
        : null,
      prepa_commentary: prepaCommentary,
    };
    await putDevice(device, deviceId);
  }

  async function fetchOrder() {
    const response = await getOrder(currentOrder.order_id);
    dispatch(setCurrentOrder(response.data));
  }

  async function fetchDevice(deviceId) {
    const response = await getDevice(deviceId);
    return await response.data;
  }

  const fetchDevices = async () => {
    if (currentOrder) {
      if (currentOrder.devices_ids) {
        const devices = [];
        const devices_id = currentOrder.devices_ids.split(",");
        for (let i = 0; i < devices_id.length; i++) {
          devices.push(await fetchDevice(devices_id[i]));
        }

        // Sort the devices array based on the device identifier
        devices.sort((a, b) => {
          // Extract the base device identifier (ignoring anything after a hyphen)
          const baseIdA = a.device_number.split(" -")[0];
          const baseIdB = b.device_number.split(" -")[0];

          // Compare the base identifiers
          if (baseIdA < baseIdB) return -1;
          if (baseIdA > baseIdB) return 1;

          // If base identifiers are equal, compare the full device_id to maintain relative order
          return a.device_number.localeCompare(b.device_number);
        });

        dispatch(setCurrentDevices(devices));
      }
    }
  };

  async function cancel() {
    fetchOrder();
    setStep(0);
    setOpen(false);
  }

  async function submit() {
    if (edit) {
      await updateDeviceAPI();
      await fetchOrder();
      await fetchDevices();
    }
    setStep(0);
    setOpen(false);
  }

  const [step, setStep] = useState(0);
  const steps = ["Contractuelle", "Planifiée", "Réelle", "Commentaire"];
  const isLastStep = step === steps.length - 1 || renderStep == null;

  const next = () => setStep(step + 1);
  const previous = () => setStep(step - 1);

  function getStepContent(stepIndex) {
    switch (stepIndex) {
      case 0:
        return (
          <PreparationContractual
            user={user}
            deviceId={deviceId}
            status={status}
            setStatus={setStatus}
            assemblyStartContractualDate={assemblyStartContractualDate}
            setAssemblyStartContractualDate={setAssemblyStartContractualDate}
            madContractualDate={madContractualDate}
            setMadContractualDate={setMadContractualDate}
            edit={edit}
          />
        );
      case 1:
        return (
          <PreparationProvisional
            assemblyStartProvisionalDate={assemblyStartProvisionalDate}
            setAssemblyStartProvisionalDate={setAssemblyStartProvisionalDate}
            madProvisionalDate={madProvisionalDate}
            setMadProvisionalDate={setMadProvisionalDate}
            edit={edit}
          />
        );
      case 2:
        return (
          <PreparationReal
            siteDeliveryRealDate={siteDeliveryRealDate}
            setSiteDeliveryRealDate={setSiteDeliveryRealDate}
            exFactoryRealDate={exFactoryRealDate}
            setExFactoryRealDate={setExFactoryRealDate}
            edit={edit}
          />
        );
      case 3:
        return (
          <PreparationCommentary
            prepaCommentary={prepaCommentary}
            setPrepaCommentary={setPrepaCommentary}
            edit={edit}
          />
        );
      default:
        return null;
    }
  }

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <>
      {edit ? (
        <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
          <Icon style={{ color: darkMode ? "#344767" : "#FFF" }}>edit</Icon>
        </IconButton>
      ) : (
        <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
          <Icon style={{ color: darkMode ? "#344767" : "#FFF" }}>add</Icon>
        </IconButton>
      )}
      <MDModal open={open} onClose={() => setOpen(false)}>
        <MDBox mt={25} mb={9}>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox
                  mt={-12}
                  mb={2}
                  mx={2}
                  variant="gradient"
                  sx={{
                    background: darkMode ? "white" : "linear-gradient(195deg, #364c70, #273956)",
                    boxShadow: darkMode ? "0 1px 6px 1px white" : "0 1px 6px 1px #344767",
                  }}
                  borderRadius="lg"
                  textAlign="center"
                >
                  <MDBox mt={2} mb={1}>
                    <MDBox>
                      <MDTypography
                        variant="h3"
                        fontWeight="bold"
                        color={darkMode ? "inherit" : "white"}
                      >
                        Modification des infomations de préparation
                      </MDTypography>
                    </MDBox>
                    <MDTypography
                      variant="h5"
                      fontWeight="regular"
                      color={darkMode ? "inherit" : "white"}
                    >
                      Veuillez remplir les informations relatives à la préparation
                    </MDTypography>
                  </MDBox>
                  <Stepper
                    sx={{
                      boxShadow: "none",
                      backgroundImage: "none",
                    }}
                    activeStep={step}
                    alternativeLabel
                  >
                    {steps.map((label) => (
                      <Step
                        sx={{
                          "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-active":
                            {
                              color: darkMode && "#344767 !important",
                            },
                          "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-completed":
                            {
                              color: darkMode && "#344767 !important",
                            },
                        }}
                        key={label}
                      >
                        <StepLabel
                          sx={{
                            "& .MuiStepLabel-label.Mui-active": {
                              color: darkMode && "#344767 !important",
                            },
                            "& .MuiStepLabel-label.Mui-completed": {
                              color: darkMode && "#344767 !important",
                            },
                            "& .MuiStepIcon-root.Mui-active": {
                              background: darkMode && "#344767",
                              fill: darkMode && "#344767",
                              stroke: darkMode && "#344767",
                              borderColor: darkMode && "#344767",
                              boxShadow: darkMode && "0rem 0rem 0rem 0.125rem #344767",
                            },
                            "& .MuiStepIcon-root.Mui-completed": {
                              background: darkMode && "#344767",
                              fill: darkMode && "#344767",
                              stroke: darkMode && "#344767",
                              borderColor: darkMode && "#344767",
                              boxShadow: darkMode && "0rem 0rem 0rem 0.125rem #344767",
                            },
                          }}
                        >
                          {label}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </MDBox>
                <MDBox p={2}>
                  <MDBox>
                    {getStepContent(step)}
                    <MDBox mt={3} width="100%" display="flex" justifyContent="space-between">
                      {step === 0 ? (
                        <MDBox />
                      ) : (
                        <MDButton variant="gradient" color="warning" onClick={previous}>
                          Précédent
                        </MDButton>
                      )}
                      <MDBox textAlign="right">
                        <Grid container spacing={1.5}>
                          <Grid item>
                            <MDButton variant="gradient" color="warning" onClick={cancel}>
                              Annuler
                            </MDButton>
                          </Grid>
                          <Grid item>
                            <MDButton
                              variant="gradient"
                              color="success"
                              onClick={!isLastStep ? next : submit}
                            >
                              {isLastStep ? "Envoyer" : "Suivant"}
                            </MDButton>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDModal>
    </>
  );
}

EditPreparation.propTypes = {
  edit: PropTypes.bool.isRequired,
};
